/**
 * Team Profile Service
 *
 * CRUD + permission/PIN operations for the team-profile sub-user layer on top
 * of vendor accounts. See `types/teamProfileTypes.ts` for the data model.
 *
 * Firestore path:  serviceProviders/{vendorId}/profiles/{profileId}
 */

import {
  collection,
  doc,
  addDoc,
  deleteDoc,
  getDoc,
  getDocs,
  updateDoc,
  setDoc,
  onSnapshot,
  query,
  where,
  Timestamp,
  type Unsubscribe,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";

import { getDb, getFunctions } from "../firebase/instance";
import { sha256Hex } from "../utils/hashPin";
import {
  ALL_CAPABILITIES,
  AVATAR_COLORS,
  DEFAULT_TEAM_MEMBER_PERMISSIONS,
  MANAGER_PERMISSIONS,
  type Capability,
  type CreateProfilePayload,
  type PinVerifyResult,
  type ProfilePermissions,
  type TeamProfile,
  type UpdateProfilePayload,
} from "../types/teamProfileTypes";

// ---------- path helpers ----------

function profilesCol(vendorId: string) {
  return collection(getDb(), "serviceProviders", vendorId, "profiles");
}

function profileDoc(vendorId: string, profileId: string) {
  return doc(getDb(), "serviceProviders", vendorId, "profiles", profileId);
}

// ---------- create / read / update / delete ----------

/**
 * Create a new profile. New Team Members start with every permission OFF;
 * new Managers get every permission ON. The "createdBy" field records the
 * profileId of whoever invoked the creation (or "system" for the very first
 * Manager seeded at account creation).
 */
export async function createProfile(
  vendorId: string,
  payload: CreateProfilePayload,
  createdBy: string
): Promise<string> {
  const permissions =
    payload.role === "manager" ? MANAGER_PERMISSIONS : DEFAULT_TEAM_MEMBER_PERMISSIONS;

  const ref = await addDoc(profilesCol(vendorId), {
    name: payload.name,
    avatar: payload.avatar,
    role: payload.role,
    permissions,
    createdAt: Timestamp.now(),
    createdBy,
  });
  return ref.id;
}

/**
 * Seed the very first Manager profile for a brand-new vendor account.
 * Called from onboarding completion or first vendor login if no profiles exist.
 * No PIN by default — manager can add one later from settings.
 */
export async function seedFirstManager(
  vendorId: string,
  name: string,
  avatarColor: string
): Promise<string> {
  return createProfile(
    vendorId,
    {
      name,
      avatar: { presetId: "default", color: avatarColor },
      role: "manager",
    },
    "system"
  );
}

/**
 * Find — or auto-create — the multi-manager CEO's owner profile inside a
 * venue they own.
 *
 * Called right after `signInWithCustomToken(venueToken)` resolves on the
 * venue picker. At that moment auth.currentUser.uid === venueId, so writes
 * to `serviceProviders/{venueId}/profiles` are allowed by the rules. The
 * profile is tagged with `primaryUid: ceoUid` so subsequent lookups (and
 * the picker's worker-visibility filter) can identify it.
 *
 * On every call:
 *  - Look up the profile by primaryUid match
 *  - If found, sync its name to the current CEO name (so a name update on
 *    the CEO's personal user doc propagates into every owned venue), then
 *    return its id
 *  - If not found, create a fresh Manager profile with full permissions
 *    and a stable color derived from the CEO uid
 *
 * Worker-visibility note: we do NOT auto-set a PIN on the profile. The CEO
 * is the only person who can ever set a PIN — and only ever through the
 * normal user-driven PIN flow that pairs it with a verified recovery phone
 * (Yuval's hard rule: "no code is ever ever set without verified phone
 * number to restore"). Instead, workers logging into the venue directly
 * are gated out of this profile via a primaryUid filter in the picker
 * (ProfilePickerClient + ProfilePickerScreen), based on whether their
 * session is the CEO impersonating or a direct venue login.
 *
 * The avatar color is derived deterministically from the CEO uid so the
 * same person looks consistent across venues without a separate "what
 * color is the owner" config to maintain.
 */
export async function findOrCreateOwnerProfile(
  venueId: string,
  primaryUid: string,
  ceoName: string
): Promise<string | null> {
  // GHOST GUARD: a venue can never own ITSELF. If the resolved primaryUid is
  // this venue's own uid, the caller's session primaryUid was poisoned (the
  // exact bug that seeded self-owned "ghost" owner profiles in prod — invisible,
  // undeletable, yet shown as the active profile). Refuse rather than create it.
  // Callers treat null as "nothing to activate" and fall back to the picker.
  if (primaryUid === venueId) return null;

  // Authoritative owner name = users/{primaryUid}.name (any authed user may read
  // it per rules; the server seed uses the same source). The passed `ceoName`
  // is derived from the caller's `user.name`, which — while impersonating a
  // sub-venue during a venue-to-venue switch — is the SUB-VENUE's name, not the
  // CEO's. Trusting it mislabels the owner profile after the source venue (the
  // "אולם א׳ shows up inside אולם ב׳" bug). Read the CEO's own doc instead.
  let ownerName = ceoName;
  try {
    const uSnap = await getDoc(doc(getDb(), "users", primaryUid));
    const n = (uSnap.data() as { name?: unknown } | undefined)?.name;
    if (typeof n === "string" && n.trim()) ownerName = n;
  } catch {
    // Keep the passed ceoName as a best-effort fallback.
  }
  if (!ownerName) ownerName = "Owner";

  const existing = (await listProfiles(venueId)).find(
    (p) => p.primaryUid === primaryUid
  );

  if (existing) {
    // Sync to the AUTHORITATIVE name (so a stale/mislabeled owner profile
    // self-heals on the next entry), never to the pollutable ceoName.
    if (existing.name !== ownerName) {
      await updateDoc(profileDoc(venueId, existing.id), { name: ownerName });
    }
    return existing.id;
  }

  // Stable color from the CEO uid so they look the same across venues.
  let hash = 0;
  for (let i = 0; i < primaryUid.length; i++) {
    hash = (hash + primaryUid.charCodeAt(i)) >>> 0;
  }
  const avatarColor = AVATAR_COLORS[hash % AVATAR_COLORS.length];

  // Deterministic doc id so the client seed and the `seedOwnerProfilesForLinkedVendors`
  // Cloud Function — which can fire concurrently on the same picker mount —
  // converge on ONE doc instead of racing to addDoc() two. setDoc+merge makes
  // the double-write idempotent. The query-by-primaryUid pass above still
  // matches legacy random-id owner docs, so no migration is needed.
  const ownerDocId = `owner_${primaryUid}`;
  await setDoc(
    profileDoc(venueId, ownerDocId),
    {
      name: ownerName,
      avatar: { presetId: "default", color: avatarColor },
      role: "manager",
      permissions: MANAGER_PERMISSIONS,
      primaryUid,
      createdAt: Timestamp.now(),
      // Mark provenance so the cleanupLegacyAutoSeededManager helper above
      // never deletes this (it only deletes "system"-created solo defaults).
      createdBy: `multi-manager:${primaryUid}`,
    },
    { merge: true }
  );
  return ownerDocId;
}

/**
 * Server-side seed for multi-manager visibility. Calls the
 * `seedOwnerProfilesForLinkedVendors` Cloud Function which, using admin SDK,
 * ensures the calling multi-manager has an owner-profile doc in every venue
 * listed in their userLinks/{primaryUid}.ownedVendorIds. Required because the
 * rule on serviceProviders/{userId}/profiles needs `auth.uid === userId` to
 * write — a multi-manager signed in as their primary uid can't write to a
 * linked venue's profiles collection directly.
 *
 * Idempotent. Safe to call on every venue picker mount. Fire and forget — a
 * transient failure (network, cold-start) just delays visibility until the
 * next picker mount.
 */
export async function seedOwnerProfilesForLinkedVendors(): Promise<{
  seeded: number;
  alreadyExisted: number;
}> {
  const callable = httpsCallable<
    Record<string, never>,
    { seeded: number; alreadyExisted: number }
  >(getFunctions(), "seedOwnerProfilesForLinkedVendors");
  const res = await callable({});
  return res.data;
}

/**
 * Migration: vendors signed up while the feature was auto-seeding a Manager
 * profile (created with `createdBy: "system"`) on every login. The new model
 * treats Team Profiles as opt-in — solo vendors should have ZERO profiles. On
 * login, if we see exactly that auto-seeded leftover (single manager profile,
 * createdBy === "system", no PIN ever set), delete it so the vendor is back in
 * true solo state and the new Team Management UI shows the "Switch to team
 * mode" empty state.
 *
 * Vendors who have meaningfully engaged with the feature (set a PIN, added
 * teammates, or created the first profile via the new explicit flow which uses
 * the vendor uid as createdBy) are untouched.
 */
export async function cleanupLegacyAutoSeededManager(vendorId: string): Promise<void> {
  const existing = await listProfiles(vendorId);
  if (existing.length !== 1) return;
  const only = existing[0];
  if (only.role !== "manager") return;
  if (only.createdBy !== "system") return;
  if (only.pinHash) return;
  await deleteProfile(vendorId, only.id);
}

export async function listProfiles(vendorId: string): Promise<TeamProfile[]> {
  const snap = await getDocs(query(profilesCol(vendorId)));
  return snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<TeamProfile, "id">) }));
}

export async function getProfile(vendorId: string, profileId: string): Promise<TeamProfile | null> {
  const snap = await getDoc(profileDoc(vendorId, profileId));
  if (!snap.exists()) return null;
  return { id: snap.id, ...(snap.data() as Omit<TeamProfile, "id">) };
}

/**
 * Subscribe to all profiles under a vendor. Used by teamProfilesStore.
 * Returns an unsubscribe function.
 */
export function subscribeToProfiles(
  vendorId: string,
  onUpdate: (profiles: TeamProfile[]) => void,
  onError?: (err: Error) => void
): Unsubscribe {
  return onSnapshot(
    query(profilesCol(vendorId)),
    (snap) => {
      const profiles = snap.docs.map((d) => ({
        id: d.id,
        ...(d.data() as Omit<TeamProfile, "id">),
      }));
      onUpdate(profiles);
    },
    (err) => {
      if (onError) onError(err);
    }
  );
}

export async function updateProfile(
  vendorId: string,
  profileId: string,
  updates: UpdateProfilePayload
): Promise<void> {
  const patch: Record<string, any> = {};
  if (updates.name !== undefined) patch.name = updates.name;
  if (updates.avatar !== undefined) patch.avatar = updates.avatar;
  if (Object.keys(patch).length === 0) return;
  await updateDoc(profileDoc(vendorId, profileId), patch);
}

export async function deleteProfile(vendorId: string, profileId: string): Promise<void> {
  await deleteDoc(profileDoc(vendorId, profileId));
}

// ---------- permissions ----------

export async function updatePermission(
  vendorId: string,
  profileId: string,
  capability: Capability,
  value: boolean
): Promise<void> {
  await updateDoc(profileDoc(vendorId, profileId), {
    [`permissions.${capability}`]: value,
  });
}

export async function grantAllPermissions(vendorId: string, profileId: string): Promise<void> {
  const next: ProfilePermissions = { ...DEFAULT_TEAM_MEMBER_PERMISSIONS };
  for (const cap of ALL_CAPABILITIES) next[cap] = true;
  await updateDoc(profileDoc(vendorId, profileId), { permissions: next });
}

export async function revokeAllPermissions(vendorId: string, profileId: string): Promise<void> {
  await updateDoc(profileDoc(vendorId, profileId), {
    permissions: { ...DEFAULT_TEAM_MEMBER_PERMISSIONS },
  });
}

// ---------- manager role transfer ----------

/**
 * Transfer the Manager role from oldManagerId to newManagerId.
 * Old manager becomes a Team Member with every permission OFF (clean handoff
 * per the agreed model — old manager must be re-granted access if desired).
 * New manager gets every permission ON. The new manager's PIN and phone are
 * cleared — they must set their own.
 *
 * NOTE: This does two writes; if the app is offline mid-transfer the state
 * could end up with two managers or zero managers. The UI surfaces this only
 * after a confirmation step, so concurrent transfers from the same device are
 * not a realistic concern. A future hardening: a single batched write.
 */
export async function transferManager(
  vendorId: string,
  oldManagerId: string,
  newManagerId: string
): Promise<void> {
  if (oldManagerId === newManagerId) return;

  await updateDoc(profileDoc(vendorId, oldManagerId), {
    role: "team_member",
    permissions: { ...DEFAULT_TEAM_MEMBER_PERMISSIONS },
    pinHash: null,
    managerPhone: null,
  });

  await updateDoc(profileDoc(vendorId, newManagerId), {
    role: "manager",
    permissions: { ...MANAGER_PERMISSIONS },
    pinHash: null,
    managerPhone: null,
  });
}

// ---------- profile PIN ----------

/**
 * Set or change a profile's PIN. Originally a manager-only flow ("Manager
 * PIN"), now open to every profile — any team member can lock their seat
 * with a 4-digit PIN so a coworker can't pick them in the picker.
 *
 * The function name and the `managerPhone` field name are left alone to
 * avoid a wide rename across the codebase; treat them as historical names
 * for "the profile's PIN" and "the profile's recovery phone." Both
 * managers and team members route through here.
 *
 * **Change-only path, server-enforced via Cloud Function.**
 *
 * A PIN must already exist on the profile (first-time set goes through
 * `verifyPinResetOTP` so the phone is SMS-verified). This function calls
 * the `setProfilePin` Cloud Function, which verifies `currentPin`
 * server-side then writes the new hash via admin SDK. Client-side direct
 * Firestore writes to `pinHash` are blocked by the firestore.rule that
 * locks the field down to admin-only.
 *
 * Why server-side: anyone holding the user's ID token could otherwise hit
 * Firestore's REST API and overwrite the PIN without proving knowledge of
 * the current one — locking the legitimate user out. The CF makes the
 * current-PIN check non-bypassable.
 *
 * `managerPhone` (optional, E.164) replaces the existing phone. To change
 * the phone with SMS RE-verification (rather than trusting the new string
 * blindly), route through `requestPinResetOTP({phone})` +
 * `verifyPinResetOTP({phone, newPin})` instead.
 */
export async function setManagerPin(
  vendorId: string,
  managerId: string,
  currentPin: string | null,
  newPin: string,
  managerPhone?: string
): Promise<void> {
  if (!/^\d{4}$/.test(newPin)) {
    throw new Error("PIN must be exactly 4 digits");
  }
  if (!currentPin) {
    throw new Error("Current PIN required to change PIN");
  }
  const callable = httpsCallable<
    {
      vendorId: string;
      managerId: string;
      currentPin: string;
      newPin: string;
      newPhone?: string;
    },
    { ok: true }
  >(getFunctions(), "setProfilePin");
  await callable({
    vendorId,
    managerId,
    currentPin,
    newPin,
    ...(managerPhone ? { newPhone: managerPhone } : {}),
  });
}

/**
 * Remove the Manager PIN. Requires the current PIN to be entered first.
 */
export async function removeManagerPin(
  vendorId: string,
  managerId: string,
  currentPin: string
): Promise<void> {
  // Routed through the `removeProfilePin` Cloud Function so the
  // currentPin check is server-enforced (can't be bypassed by a stolen
  // token writing pinHash=null via direct Firestore REST).
  const callable = httpsCallable<
    { vendorId: string; managerId: string; currentPin: string },
    { ok: true }
  >(getFunctions(), "removeProfilePin");
  await callable({ vendorId, managerId, currentPin });
}

/**
 * Verify a candidate PIN. Returns:
 *  - "ok"          — matches
 *  - "wrong"       — doesn't match
 *  - "no_pin_set"  — manager has no PIN configured; caller should let them in
 */
export async function verifyManagerPin(
  vendorId: string,
  managerId: string,
  candidatePin: string
): Promise<PinVerifyResult> {
  const existing = await getProfile(vendorId, managerId);
  if (!existing) throw new Error("Manager profile not found");
  if (!existing.pinHash) return "no_pin_set";
  const candidateHash = await sha256Hex(candidatePin);
  return candidateHash === existing.pinHash ? "ok" : "wrong";
}

// ---------- manager PIN recovery (SMS OTP via Cloud Function) ----------

interface RequestOTPResponse {
  ok: true;
  expiresInSeconds: number;
  phoneTail?: string;
}

interface VerifyOTPResponse {
  ok: true;
}

/**
 * Send a 6-digit OTP to the manager's phone. Backed by `sendPinResetOTP`.
 * Pass `phone` to send the code to a specific number (used by PIN-setup /
 * phone-change flows to verify a phone before persisting it). Omit `phone`
 * and the CF sends to the manager's already-stored `managerPhone` (recovery).
 */
export async function requestPinResetOTP(
  vendorId: string,
  managerId: string,
  phone?: string
): Promise<RequestOTPResponse> {
  const callable = httpsCallable<
    { vendorId: string; managerId: string; phone?: string },
    RequestOTPResponse
  >(getFunctions(), "sendPinResetOTP");
  const res = await callable({ vendorId, managerId, ...(phone ? { phone } : {}) });
  return res.data;
}

/**
 * Verify an OTP and set a new PIN. Pass `phone` to also persist that phone as
 * the manager's new `managerPhone` — used when setting up or changing a phone
 * alongside the PIN. Recovery flow omits `phone`.
 */
export async function verifyPinResetOTP(
  vendorId: string,
  managerId: string,
  code: string,
  newPin: string,
  phone?: string
): Promise<VerifyOTPResponse> {
  const callable = httpsCallable<
    { vendorId: string; managerId: string; code: string; newPin: string; phone?: string },
    VerifyOTPResponse
  >(getFunctions(), "verifyPinResetOTP");
  const res = await callable({
    vendorId,
    managerId,
    code,
    newPin,
    ...(phone ? { phone } : {}),
  });
  return res.data;
}

// ---------- disband team (email OTP) ----------

interface RequestDisbandOTPResponse {
  ok: true;
  expiresInSeconds: number;
  emailTail: string;
}

/**
 * Send a 6-digit OTP to the venue's Firebase Auth email. Server-side
 * guardrail: the vendor must have exactly one profile (the Manager) — remove
 * all team members first. Backed by `sendDisbandOTP`.
 */
export async function requestDisbandOTP(vendorId: string): Promise<RequestDisbandOTPResponse> {
  const callable = httpsCallable<{ vendorId: string }, RequestDisbandOTPResponse>(
    getFunctions(),
    "sendDisbandOTP"
  );
  const res = await callable({ vendorId });
  return res.data;
}

/**
 * Verify the OTP and disband the team. The Manager profile is deleted,
 * returning the vendor to true solo state. Past `actorProfileId` stamps on
 * historical writes survive as dangling labels — that's by design.
 */
export async function verifyDisbandOTP(
  vendorId: string,
  code: string
): Promise<VerifyOTPResponse> {
  const callable = httpsCallable<{ vendorId: string; code: string }, VerifyOTPResponse>(
    getFunctions(),
    "verifyDisbandOTP"
  );
  const res = await callable({ vendorId, code });
  return res.data;
}

// ---------- per-profile work email (for outbound mail capture) ----------

/**
 * Set or update the work email address for a profile. This is the address
 * the profile sends customer replies from in their own mail client; the
 * outbound-mail capture pipeline matches sent items back to this profile
 * via this field.
 *
 * Normalized to lowercase + trimmed. Pass an empty string to clear.
 */
export async function setProfileWorkEmail(
  vendorId: string,
  profileId: string,
  email: string
): Promise<void> {
  const normalized = email.trim().toLowerCase();
  await updateDoc(profileDoc(vendorId, profileId), {
    workEmail: normalized || null,
  });
}

export async function clearProfileWorkEmail(
  vendorId: string,
  profileId: string
): Promise<void> {
  await updateDoc(profileDoc(vendorId, profileId), { workEmail: null });
}

// ---------- cross-venue seats (Layer 2) ----------

export type SetVenueSeatResult =
  | { ok: true; status: "granted" | "revoked" }
  | {
      ok: true;
      status: "candidates";
      candidates: { id: string; name: string; avatar: TeamProfile["avatar"] }[];
    };

/**
 * Grant or revoke a team member's seat in another venue of the same business
 * group. Server-authoritative (setVenueSeat CF) — all seat fields are locked to
 * server writes. On a first grant where the target venue has adoptable local
 * profiles and neither `adoptProfileId` nor `createNew` is passed, the CF
 * returns `{ status: "candidates" }` so the UI can offer "create new seat" vs
 * "this is <existing profile>".
 */
export async function setVenueSeat(params: {
  vendorId: string;
  profileId: string;
  targetVendorId: string;
  enabled: boolean;
  adoptProfileId?: string;
  createNew?: boolean;
}): Promise<SetVenueSeatResult> {
  const callable = httpsCallable<typeof params, SetVenueSeatResult>(
    getFunctions(),
    "setVenueSeat"
  );
  const res = await callable(params);
  return res.data;
}

/**
 * All OTHER venue ids in the same business group as `vendorId` (excludes itself).
 * Drives the Team-Management seat-toggle list. Resolves the group both ways:
 * `vendorId` as the CEO key of its own userLinks doc, and as a member of some
 * CEO's ownedVendorIds. A direct venue login can read the CEO's userLinks doc
 * because the rule allows it when the caller's uid is in ownedVendorIds.
 * Returns [] for a solo vendor not in any group.
 */
export async function getBusinessGroupVenueIds(vendorId: string): Promise<string[]> {
  const db = getDb();
  const out = new Set<string>();
  const asKey = await getDoc(doc(db, "userLinks", vendorId));
  if (asKey.exists()) {
    ((asKey.data()?.ownedVendorIds as string[] | undefined) || []).forEach((id) => out.add(id));
  }
  const memberSnap = await getDocs(
    query(collection(db, "userLinks"), where("ownedVendorIds", "array-contains", vendorId))
  );
  memberSnap.forEach((d) => {
    out.add(d.id);
    ((d.data()?.ownedVendorIds as string[] | undefined) || []).forEach((id) => out.add(id));
  });
  out.delete(vendorId);
  return Array.from(out);
}

/**
 * After signInWithCustomToken swaps the session into a seat venue, find the
 * person's counterpart profile there (by personKey), skipping a disabled seat,
 * so the caller can setActiveProfile onto it. Returns null if none is active.
 */
export async function resolveSeatLanding(
  vendorId: string,
  personKey: string
): Promise<string | null> {
  const snap = await getDocs(
    query(profilesCol(vendorId), where("personKey", "==", personKey))
  );
  const match = snap.docs
    .map((d) => ({ id: d.id, ...(d.data() as Omit<TeamProfile, "id">) }))
    .find((p) => !p.seatDisabled);
  return match ? match.id : null;
}

// ---------- activity heartbeat (for picker "Active 2h ago") ----------

/**
 * Bump lastActiveAt on the active profile. Called sparingly — once on profile
 * select and then on app foreground to avoid hot writes. Not transactional;
 * a missed bump is acceptable.
 */
export async function recordProfileActive(vendorId: string, profileId: string): Promise<void> {
  await setDoc(
    profileDoc(vendorId, profileId),
    { lastActiveAt: Timestamp.now() },
    { merge: true }
  );
}
